// Keep Fit App - 統計頁面

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getStats } from '../services/api';
import type { DailySummary } from '../types';
import { DEFAULT_TARGETS } from '../types';

export default function Stats() {
  const [period, setPeriod] = useState<'day' | 'week' | 'month'>('week');

  const { data, isLoading, error } = useQuery({
    queryKey: ['stats', period],
    queryFn: () => getStats(period),
  });

  const stats = data?.data || [];

  // 計算平均值
  const avgCalories = stats.length > 0
    ? Math.round(stats.reduce((sum, s) => sum + s.totalCalories, 0) / stats.length)
    : 0;
  const avgProtein = stats.length > 0
    ? (stats.reduce((sum, s) => sum + s.totalProtein, 0) / stats.length).toFixed(1)
    : '0';
  const avgFat = stats.length > 0
    ? (stats.reduce((sum, s) => sum + s.totalFat, 0) / stats.length).toFixed(1)
    : '0';
  const avgCarbs = stats.length > 0
    ? (stats.reduce((sum, s) => sum + s.totalCarbs, 0) / stats.length).toFixed(1)
    : '0';

  // 找出最高/最低卡路里日
  const maxDay = stats.reduce((max, s) => 
    s.totalCalories > (max?.totalCalories || 0) ? s : max, 
    stats[0]
  );
  const minDay = stats.reduce((min, s) => 
    s.totalCalories < (min?.totalCalories || Infinity) ? s : min, 
    stats[0]
  );

  // 簡單柱狀圖
  const BarChart = ({ data, maxValue }: { data: DailySummary[]; maxValue: number }) => {
    if (data.length === 0) return null;

    return (
      <div className="flex items-end gap-1 h-32 overflow-x-auto pb-2">
        {data.map((day) => {
          const height = maxValue > 0 ? (day.totalCalories / maxValue) * 100 : 0;
          const isOverTarget = day.totalCalories > DEFAULT_TARGETS.calories;
          
          return (
            <div
              key={day.date}
              className="flex-1 min-w-[24px] flex flex-col items-center"
            >
              <div
                className={`w-full rounded-t transition-all ${
                  isOverTarget ? 'bg-red-400' : 'bg-green-400'
                }`}
                style={{ height: `${Math.max(height, 4)}%` }}
              />
              <div className="text-xs text-gray-400 mt-1 truncate">
                {new Date(day.date).getDate()}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('zh-TW', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-gray-800">📊 營養統計</h1>

      {/* 時間範圍選擇 */}
      <div className="flex bg-white rounded-xl p-1 shadow-sm">
        {[
          { value: 'day', label: '今日' },
          { value: 'week', label: '本週' },
          { value: 'month', label: '本月' },
        ].map((p) => (
          <button
            key={p.value}
            onClick={() => setPeriod(p.value as typeof period)}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
              period === p.value
                ? 'bg-green-500 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-400">載入中...</div>
      ) : error ? (
        <div className="text-center py-8 text-red-500">載入失敗</div>
      ) : stats.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <div className="text-4xl mb-2">📊</div>
          <p>暫無統計數據</p>
          <p className="text-sm">開始記錄飲食後即可查看統計</p>
        </div>
      ) : (
        <>
          {/* 平均值概覽 */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h2 className="font-medium text-gray-500 mb-3">平均每日攝取</h2>
            <div className="grid grid-cols-4 gap-3">
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{avgCalories}</div>
                <div className="text-xs text-gray-500">卡路里</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{avgProtein}g</div>
                <div className="text-xs text-gray-500">蛋白質</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{avgFat}g</div>
                <div className="text-xs text-gray-500">脂肪</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{avgCarbs}g</div>
                <div className="text-xs text-gray-500">碳水</div>
              </div>
            </div>
          </div>

          {/* 卡路里趨勢圖 */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex justify-between items-center mb-3">
              <h2 className="font-medium">卡路里趨勢</h2>
              <span className="text-xs text-gray-400">
                目標: {DEFAULT_TARGETS.calories} kcal
              </span>
            </div>
            <BarChart 
              data={stats} 
              maxValue={Math.max(DEFAULT_TARGETS.calories, ...stats.map(s => s.totalCalories))}
            />
          </div>

          {/* 最高/最低 */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-red-50 rounded-xl p-4">
              <div className="text-sm text-gray-500 mb-1">最高卡路里</div>
              <div className="font-bold text-red-600">{maxDay?.totalCalories || 0} kcal</div>
              {maxDay && (
                <div className="text-xs text-gray-400">{formatDate(maxDay.date)}</div>
              )}
            </div>
            <div className="bg-green-50 rounded-xl p-4">
              <div className="text-sm text-gray-500 mb-1">最低卡路里</div>
              <div className="font-bold text-green-600">{minDay?.totalCalories || 0} kcal</div>
              {minDay && (
                <div className="text-xs text-gray-400">{formatDate(minDay.date)}</div>
              )}
            </div>
          </div>

          {/* 詳細數據表 */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b font-medium">
              詳細數據
            </div>
            <div className="divide-y max-h-64 overflow-y-auto">
              {stats.map((day) => (
                <div key={day.date} className="p-3 flex justify-between items-center">
                  <div>
                    <div className="font-medium">{formatDate(day.date)}</div>
                    <div className="text-xs text-gray-400">
                      {day.recordCount} 項記錄
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-orange-600">
                      {day.totalCalories} kcal
                    </div>
                    <div className="text-xs text-gray-400">
                      P:{Math.round(day.totalProtein)}g 
                      F:{Math.round(day.totalFat)}g 
                      C:{Math.round(day.totalCarbs)}g
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
